import { useEffect, useState } from "react";
import type { GraphyClient } from "../api/client";
import { ApiError } from "../api/client";
import type { WikiArticle, WikiIndexItem, WikiSection } from "../api/types";
import { EmptyState, ErrorState, Loading } from "../components/states";
import { Icon } from "../components/icons";
import { T } from "../i18n";

// GY-050 architecture wiki: index + article navigation, source-drawer links,
// explore-this-area / ask-about-this-area actions, and the knowledge lifecycle
// (review, source-drift staleness, regenerate preview). Source drift never
// silently replaces reviewed content — the preview is accepted explicitly.

interface Props {
  client: GraphyClient;
  projectId: string;
  onViewSource: (symbolId: string) => void;
  onExploreCommunity: (communityId: number) => void;
}

export function Wiki({ client, projectId, onViewSource, onExploreCommunity }: Props) {
  const [idx, setIdx] = useState<WikiIndexItem[] | null>(null);
  const [current, setCurrent] = useState<WikiArticle | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    client
      .wikiIndex({ projectId })
      .then((d) => live && setIdx(d))
      .catch((e) => {
        if (e instanceof ApiError || e instanceof Error) setError(true);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [client, projectId]);

  const open = async (articleId: string) => {
    try {
      setCurrent(await client.wikiArticle({ projectId }, articleId));
    } catch {
      setError(true);
    }
  };

  const refreshIndex = async () => setIdx(await client.wikiIndex({ projectId }));

  if (error) return <ErrorState />;
  if (loading || !idx) return <Loading />;
  if (idx.length === 0) return <EmptyState message={T.wiki.none} />;

  if (current) {
    return (
      <ArticleView
        article={current}
        onBack={() => setCurrent(null)}
        onViewSource={onViewSource}
        onExploreCommunity={onExploreCommunity}
        onReview={async () => {
          const a = await client.wikiReview({ projectId, articleId: current.articleId });
          setCurrent(a);
          void refreshIndex();
        }}
        onRegeneration={async (action) => {
          const a = await client.wikiRegeneration({ projectId, articleId: current.articleId, action });
          setCurrent(a);
          void refreshIndex();
        }}
      />
    );
  }

  return (
    <div className="wiki-index">
      <h2>{T.wiki.index}</h2>
      <ul className="node-list">
        {idx.map((a) => (
          <li key={a.articleId}>
            <button className="link" onClick={() => open(a.articleId)}>
              {a.title}
            </button>
            <span>
              <span className="tag">{T.wiki.reviewState[a.reviewState]}</span>
              {a.stale && <span className="tag tag-high">{T.wiki.staleBadge}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArticleView({
  article,
  onBack,
  onViewSource,
  onExploreCommunity,
  onReview,
  onRegeneration,
}: {
  article: WikiArticle;
  onBack: () => void;
  onViewSource: (symbolId: string) => void;
  onExploreCommunity: (communityId: number) => void;
  onReview: () => void;
  onRegeneration: (action: "accept" | "discard") => void;
}) {
  const [askNote, setAskNote] = useState(false);
  return (
    <div className="wiki-article">
      <button className="link btn-icon" onClick={onBack}>
        <Icon.chevron size={13} className="flip-x" />
        {T.wiki.back}
      </button>
      <div className="stat-row">
        <h2>{article.title}</h2>
        <span className="tag">{T.wiki.reviewState[article.reviewState]}</span>
        <span className="tag">{T.wiki.snapshot(article.snapshotId)}</span>
        {article.reviewedBy && <span className="muted">{T.wiki.reviewedBy(article.reviewedBy)}</span>}
      </div>

      {article.stale && article.pendingPreview && (
        <div className="card">
          <div className="banner banner-partial">{T.wiki.stale}</div>
          <p className="muted">{T.wiki.staleHint}</p>
          <h4>{T.wiki.preview}</h4>
          {article.pendingPreview.sections.map((s, i) => (
            <Section key={i} section={s} onViewSource={onViewSource} />
          ))}
          <div className="inspector-actions">
            <button className="btn selected-tab" onClick={() => onRegeneration("accept")}>
              {T.wiki.accept}
            </button>
            <button className="btn btn-ghost" onClick={() => onRegeneration("discard")}>
              {T.wiki.discard}
            </button>
          </div>
        </div>
      )}

      {article.sections.map((s, i) => (
        <Section key={i} section={s} onViewSource={onViewSource} />
      ))}

      <div className="inspector-actions">
        {article.reviewState !== "reviewed" && (
          <button className="btn" onClick={onReview}>
            {T.wiki.review}
          </button>
        )}
        {article.communityId !== null && (
          <button className="btn" onClick={() => onExploreCommunity(article.communityId!)}>
            {T.wiki.exploreArea}
          </button>
        )}
        <button className="btn btn-ghost" onClick={() => setAskNote(true)}>
          {T.wiki.askArea}
        </button>
      </div>
      {askNote && <div className="banner banner-partial">{T.wiki.askUnavailable}</div>}
    </div>
  );
}

function Section({ section, onViewSource }: { section: WikiSection; onViewSource: (id: string) => void }) {
  return (
    <section className="card">
      <h3>{section.heading}</h3>
      <p>{section.body}</p>
      {section.sourceRefs.length > 0 && (
        <>
          <h4 className="muted">{T.wiki.sources}</h4>
          <div className="wiki-sources">
            {section.sourceRefs.map((r) => (
              <button key={r.symbolId} className="link" onClick={() => onViewSource(r.symbolId)}>
                {r.path}:{r.startLine}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
